import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/min';
import type { TranslationKey } from '../i18n';
import { E164_REGEX } from './phone';

export type StoreContactCountryCode = 'IL' | 'PS' | 'JO' | 'SY' | 'LB' | 'EG' | 'TR' | 'US';
export type StoreContactCountrySelectValue = StoreContactCountryCode | 'INTERNATIONAL';

export interface StoreContactCountryOption {
  readonly code: StoreContactCountrySelectValue;
  readonly dialCode: string;
  readonly flag: string;
  readonly translationKey: TranslationKey;
  readonly isInternationalFallback?: boolean;
}

export interface StoreContactPhoneFieldInput {
  readonly country: StoreContactCountrySelectValue;
  readonly localNumber: string;
}

export interface StoreContactPhoneRehydrated {
  readonly country: StoreContactCountrySelectValue;
  readonly localNumber: string;
  readonly callingCode: string;
  readonly e164: string | null;
  readonly isInternationalFallback: boolean;
}

export type StoreContactPhoneNormalizeError =
  | 'invalidForCountry'
  | 'invalidInternational'
  | 'invalidE164';

export type StoreContactPhoneNormalizeResult =
  | { readonly ok: true; readonly e164: string | null }
  | { readonly ok: false; readonly reason: StoreContactPhoneNormalizeError };

export const STORE_CONTACT_DEFAULT_COUNTRY: StoreContactCountryCode = 'IL';
export const STORE_CONTACT_INTERNATIONAL_COUNTRY: StoreContactCountrySelectValue = 'INTERNATIONAL';
export const STORE_CONTACT_LOCAL_INPUT_MAX_LENGTH = 64;
export const STORE_CONTACT_INTERNATIONAL_INPUT_MAX_LENGTH = 16;

const STORE_CONTACT_DEFAULT_COUNTRY_OPTION: StoreContactCountryOption = {
  code: STORE_CONTACT_DEFAULT_COUNTRY,
  dialCode: '+972',
  flag: '🇮🇱',
  translationKey: 'common.phoneCountry.IL',
};

export const STORE_CONTACT_SUPPORTED_COUNTRIES: readonly StoreContactCountryOption[] = [
  STORE_CONTACT_DEFAULT_COUNTRY_OPTION,
  { code: 'PS', dialCode: '+970', flag: '🇵🇸', translationKey: 'common.phoneCountry.PS' },
  { code: 'JO', dialCode: '+962', flag: '🇯🇴', translationKey: 'common.phoneCountry.JO' },
  { code: 'SY', dialCode: '+963', flag: '🇸🇾', translationKey: 'common.phoneCountry.SY' },
  { code: 'LB', dialCode: '+961', flag: '🇱🇧', translationKey: 'common.phoneCountry.LB' },
  { code: 'EG', dialCode: '+20', flag: '🇪🇬', translationKey: 'common.phoneCountry.EG' },
  { code: 'TR', dialCode: '+90', flag: '🇹🇷', translationKey: 'common.phoneCountry.TR' },
  { code: 'US', dialCode: '+1', flag: '🇺🇸', translationKey: 'common.phoneCountry.US' },
  {
    code: STORE_CONTACT_INTERNATIONAL_COUNTRY,
    dialCode: '',
    flag: '🌐',
    translationKey: 'common.phoneCountry.international',
    isInternationalFallback: true,
  },
] as const;

const STORE_CONTACT_CURATED_COUNTRY_CODES: readonly StoreContactCountryCode[] = [
  'IL',
  'PS',
  'JO',
  'SY',
  'LB',
  'EG',
  'TR',
  'US',
];

function isCuratedCountry(code: string | undefined): code is StoreContactCountryCode {
  return STORE_CONTACT_CURATED_COUNTRY_CODES.some((country) => country === code);
}

export function getStoreContactCountryOption(
  code: StoreContactCountrySelectValue,
): StoreContactCountryOption {
  return (
    STORE_CONTACT_SUPPORTED_COUNTRIES.find((country) => country.code === code) ??
    STORE_CONTACT_DEFAULT_COUNTRY_OPTION
  );
}

export function getStoreContactCallingCode(country: StoreContactCountrySelectValue): string {
  return getStoreContactCountryOption(country).dialCode;
}

export function rehydrateStoreContactPhone(e164: string | null): StoreContactPhoneRehydrated {
  if (e164 === null || e164.trim() === '') {
    return {
      country: STORE_CONTACT_DEFAULT_COUNTRY,
      localNumber: '',
      callingCode: getStoreContactCallingCode(STORE_CONTACT_DEFAULT_COUNTRY),
      e164: null,
      isInternationalFallback: false,
    };
  }

  const trimmed = e164.trim();
  if (!E164_REGEX.test(trimmed)) {
    return {
      country: STORE_CONTACT_INTERNATIONAL_COUNTRY,
      localNumber: trimmed,
      callingCode: '',
      e164: null,
      isInternationalFallback: true,
    };
  }

  const parsed = parsePhoneNumberFromString(trimmed);
  if (parsed?.isValid() && isCuratedCountry(parsed.country)) {
    const country = parsed.country;
    return {
      country,
      localNumber: parsed.formatNational(),
      callingCode: getStoreContactCallingCode(country),
      e164: parsed.number,
      isInternationalFallback: false,
    };
  }

  return {
    country: STORE_CONTACT_INTERNATIONAL_COUNTRY,
    localNumber: trimmed,
    callingCode: '',
    e164: trimmed,
    isInternationalFallback: true,
  };
}

export function normalizeStoreContactPhone(
  input: StoreContactPhoneFieldInput,
): StoreContactPhoneNormalizeResult {
  const trimmed = input.localNumber.trim();

  if (input.country === STORE_CONTACT_INTERNATIONAL_COUNTRY) {
    if (trimmed === '') return { ok: true, e164: null };
    if (trimmed.length > STORE_CONTACT_INTERNATIONAL_INPUT_MAX_LENGTH) {
      return { ok: false, reason: 'invalidInternational' };
    }
    return E164_REGEX.test(trimmed)
      ? { ok: true, e164: trimmed }
      : { ok: false, reason: 'invalidInternational' };
  }

  if (trimmed === '') return { ok: true, e164: null };
  if (trimmed.length > STORE_CONTACT_LOCAL_INPUT_MAX_LENGTH) {
    return { ok: false, reason: 'invalidForCountry' };
  }

  const parsed = parsePhoneNumberFromString(trimmed, input.country as CountryCode);
  if (!parsed?.isValid() || parsed.country !== input.country || !E164_REGEX.test(parsed.number)) {
    return { ok: false, reason: 'invalidForCountry' };
  }

  return { ok: true, e164: parsed.number };
}
